/* ============================================================
   <video-popup> — ouvre la vidéo dans une modale au clic sur le poster.
   Markup attendu :
     <video-popup data-loop="true">
       <button class="video-popup__trigger">...poster + .video-popup__play...</button>
       <template class="video-popup__tpl"><video ...></video></template>
     </video-popup>
   La vidéo n'est créée qu'à l'ouverture (pas de chargement avant le clic),
   et détruite à la fermeture (coupe le son, libère le réseau).
   ============================================================ */
// Chargé une fois par vidéo popup de la page : portée fermée, sinon la deuxième
// exécution lève « Identifier 'VideoPopup' has already been declared ».
(() => {
if (customElements.get("video-popup")) return;

class VideoPopup extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.trigger = this.querySelector(".video-popup__trigger");
    this.tpl = this.querySelector("template.video-popup__tpl");
    if (!this.trigger || !this.tpl) return;
    this.trigger.addEventListener("click", () => this.open());
    this._onKey = (e) => {
      if (e.key === "Escape") this.close();
    };
  }

  open() {
    if (this.backdrop) return;
    const backdrop = document.createElement("div");
    backdrop.className = "video-popup__backdrop";

    const dialog = document.createElement("div");
    dialog.className = "video-popup__dialog";

    const close = document.createElement("button");
    close.type = "button";
    close.className = "video-popup__close";
    close.setAttribute("aria-label", "Fermer la vidéo");
    close.innerHTML =
      '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

    const node = this.tpl.content.cloneNode(true);
    dialog.appendChild(node);
    dialog.appendChild(close);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);
    document.body.style.overflow = "hidden";

    this.backdrop = backdrop;
    this.video = dialog.querySelector("video");

    // Dimensionne la vidéo pour remplir la fenêtre en gardant son ratio
    // (lightbox) : pas de bandes noires, et on n'est pas bloqué à la
    // résolution native d'une vidéo basse déf.
    this._fit = () => {
      if (!this.video) return;
      const r = this.video.videoWidth / this.video.videoHeight;
      if (!r || !isFinite(r)) return;
      const maxW = window.innerWidth - 48;
      const maxH = window.innerHeight - 48;
      let w = maxW;
      let h = w / r;
      if (h > maxH) {
        h = maxH;
        w = h * r;
      }
      this.video.style.width = Math.round(w) + "px";
      this.video.style.height = Math.round(h) + "px";
    };
    if (this.video.readyState >= 1) this._fit();
    else
      this.video.addEventListener("loadedmetadata", this._fit, { once: true });
    window.addEventListener("resize", this._fit);

    // ouverture animée
    requestAnimationFrame(() => backdrop.classList.add("is-open"));

    if (this.video) {
      const p = this.video.play();
      if (p && p.catch) p.catch(() => {});
    }

    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) this.close();
    });
    close.addEventListener("click", () => this.close());
    document.addEventListener("keydown", this._onKey);
    close.focus();
  }

  close() {
    if (!this.backdrop) return;
    const backdrop = this.backdrop;
    this.backdrop = null;
    document.removeEventListener("keydown", this._onKey);
    if (this._fit) window.removeEventListener("resize", this._fit);
    if (this.video) {
      try {
        this.video.pause();
      } catch (e) {}
      this.video.removeAttribute("src");
      this.video = null;
    }
    backdrop.classList.remove("is-open");
    document.body.style.overflow = "";
    setTimeout(() => backdrop.remove(), 250);
    if (this.trigger) this.trigger.focus();
  }
}

if (!customElements.get("video-popup")) {
  customElements.define("video-popup", VideoPopup);
}
})();
